
import { useState, useEffect } from "react"
import { useForm, Controller } from "react-hook-form"
import { Listbox } from '@headlessui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { zodResolver } from "@hookform/resolvers/zod"
import { projectFormSchema, type ProjectFormValues, type ProjectMeta, type Project } from "../hooks/types"

interface ProjectFormDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: ProjectFormValues) => void
  isLoading: boolean
  projectMeta?: ProjectMeta
  isMetaLoading: boolean
  defaultValues?: Partial<ProjectFormValues>
  mode: "create" | "edit"
  project?: Project
}

export default function ProjectFormDialog({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  projectMeta,
  isMetaLoading,
  defaultValues,
  mode,
}: ProjectFormDialogProps) {
  const form = useForm<ProjectFormValues>({
    // TEMP: cast schema to TODO to avoid deep-instantiation errors; refine schema typings later
    resolver: zodResolver(projectFormSchema as TODO),
    defaultValues: defaultValues || {
      projectCode: "",
      title: "",
      clientId: "",
      location: "",
      status: "Planning",
      projectManagerPersonnelId: "",
      foremanPersonnelIds: [],
      budget: {
        currency: projectMeta?.orgDefaults.currency || "USD",
      },
    },
  })

  // Update form when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues)
    }
  }, [defaultValues, form])

  const [activeTab, setActiveTab] = useState("basic-info")
  // Filters for searchable dropdowns
  const [pmFilter, setPmFilter] = useState("")
  const [foreFilter, setForeFilter] = useState("")

  const handleSubmit = (data: ProjectFormValues) => {
    onSubmit(data)
  }

  if (!isOpen) return null
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center overflow-y-auto z-50 p-4">
      <div className="bg-white text-gray-900 rounded-lg w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{mode === "create" ? "Create New Project" : "Edit Project"}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              &times;
            </button>
          </div>

          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="mb-6">
              <div className="flex space-x-2 border-b">
                <button
                  type="button"
                  className={`py-2 px-4 ${
                    activeTab === "basic-info"
                      ? "border-b-2 border-blue-500 text-blue-600 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab("basic-info")}
                >
                  Basic Info
                </button>
                <button
                  type="button"
                  className={`py-2 px-4 ${
                    activeTab === "seats"
                      ? "border-b-2 border-blue-500 text-blue-600 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab("seats")}
                >
                  Seats
                </button>
                <button
                  type="button"
                  className={`py-2 px-4 ${
                    activeTab === "budget"
                      ? "border-b-2 border-blue-500 text-blue-600 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab("budget")}
                >
                  Budget
                </button>
                <button
                  type="button"
                  className={`py-2 px-4 ${
                    activeTab === "review"
                      ? "border-b-2 border-blue-500 text-blue-600 font-semibold"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => setActiveTab("review")}
                >
                  Review
                </button>
              </div>
            </div>

            {/* Basic Info Tab */}
            {activeTab === "basic-info" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Project Code *</label>
                    <input
                      {...form.register("projectCode")}
                      className="w-full border border-gray-300 p-2 rounded text-gray-900"
                      placeholder="Enter project code"
                      disabled={isLoading}
                    />
                    {form.formState.errors.projectCode && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.projectCode.message}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Internal reference for this project</p>
                  </div>

                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Title *</label>
                    <input
                      {...form.register("title")}
                      className="w-full border border-gray-300 p-2 rounded text-gray-900"
                      placeholder="Enter project title"
                      disabled={isLoading}
                    />
                    {form.formState.errors.title && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.title.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Client *</label>
                  <select
                    {...form.register("clientId")}
                    className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                    disabled={isLoading || isMetaLoading}
                  >
                    <option value="">Select a client</option>
                    {projectMeta?.clients.map((client) => (
                      <option key={client._id} value={client._id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {form.formState.errors.clientId && (
                    <p className="text-red-500 text-sm mt-1">{form.formState.errors.clientId.message}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Location</label>
                  <input
                    {...form.register("location")}
                    className="w-full border border-gray-300 p-2 rounded text-gray-900"
                    placeholder="Enter project location"
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Status</label>
                  <select
                    {...form.register("status")}
                    className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                    disabled={isLoading}
                  >
                    {[
                      "Planning",
                      "Bidding",
                      "Awarded",
                      "Fabrication",
                      "Erection",
                      "Completed",
                      "OnHold",
                      "Cancelled",
                    ].map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Bid Date</label>
                    <input
                      type="date"
                      {...form.register("bidDate")}
                      className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Award Date</label>
                    <input
                      type="date"
                      {...form.register("awardDate")}
                      className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Fabrication Start Date</label>
                    <input
                      type="date"
                      {...form.register("fabricationStartDate")}
                      className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Fabrication End Date</label>
                    <input
                      type="date"
                      {...form.register("fabricationEndDate")}
                      className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Erection Start Date</label>
                    <input
                      type="date"
                      {...form.register("erectionStartDate")}
                      className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Erection End Date</label>
                    <input
                      type="date"
                      {...form.register("erectionEndDate")}
                      className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block mb-1 text-gray-700 font-medium">Completion Date</label>
                  <input
                    type="date"
                    {...form.register("completionDate")}
                    className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                    disabled={isLoading}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setActiveTab("seats")}
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Seats Tab */}
            {activeTab === "seats" && (
              <div className="space-y-4">
                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Project Manager *</label>
                  <Controller
                    control={form.control}
                    name="projectManagerPersonnelId"
                    rules={{ required: 'Please select a project manager.' }}
                    render={({ field }) => (
                      <div className="relative">
                        <Listbox
                          value={field.value || ''}
                          onChange={field.onChange}
                          disabled={isLoading || isMetaLoading}
                        >
                          <Listbox.Button className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900 flex justify-between items-center">
                            {field.value
                              ? projectMeta?.personnelByRole['Project Manager']
                                  ?.find(p => p._id === field.value)
                                  ?.contactId.name
                              : 'Select a project manager'}
                            <ChevronsUpDown className="w-5 h-5 text-gray-600 ml-2" />
                          </Listbox.Button>
                          <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 overflow-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="px-3 py-2">
                              <input
                                type="text"
                                value={pmFilter}
                                onChange={e => setPmFilter(e.target.value)}
                                placeholder="Search foreman..."
                                className="w-full border border-gray-300 p-1 rounded text-gray-900"
                              />
                            </div>
                            {projectMeta?.personnelByRole['Project Manager']
                              ?.filter(p =>
                                p.contactId.name.toLowerCase().includes(pmFilter.toLowerCase()),
                              )
                              .map(pm => (
                                <Listbox.Option
                                  key={pm._id}
                                  value={pm._id}
                                  className={({ active }) =>
                                    `cursor-pointer select-none relative py-2 pl-10 pr-4 ${
                                      active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={`block truncate ${
                                          selected ? 'font-semibold' : 'font-normal'
                                        }`}
                                      >
                                        {pm.contactId.name}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                          <Check className="w-5 h-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                          </Listbox.Options>
                        </Listbox>
                        {form.formState.errors.projectManagerPersonnelId && (
                          <p className="text-red-500 text-sm mt-1">
                            {form.formState.errors.projectManagerPersonnelId.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Foremen</label>
                  <Controller
                    control={form.control}
                    name="foremanPersonnelIds"
                    render={({ field }) => (
                      <div className="relative">
                        <Listbox
                          value={Array.isArray(field.value) ? field.value[0] : ''}
                          onChange={(val) => field.onChange([val])}
                          disabled={isLoading || isMetaLoading}
                        >
                          <Listbox.Button className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900 flex justify-between items-center">
                            {Array.isArray(field.value) && field.value.length > 0
                              ? projectMeta?.personnelByRole['Foreman']
                                  ?.filter(p => (field.value ?? []).includes(p._id))
                                  .map(p => p.contactId.name)
                                  .join(', ')
                              : 'Select foreman'}
                            <ChevronsUpDown className="w-5 h-5 text-gray-600 ml-2" />
                          </Listbox.Button>
                          <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 overflow-auto rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="px-3 py-2">
                              <input
                                type="text"
                                value={foreFilter}
                                onChange={e => setForeFilter(e.target.value)}
                                placeholder="Search project manager..."
                                className="w-full border border-gray-300 p-1 rounded text-gray-900"
                              />
                            </div>
                            {projectMeta?.personnelByRole['Foreman']
                              ?.filter(p => p.contactId.name.toLowerCase().includes(foreFilter.toLowerCase()))
                              .map((foreman) => (
                              <Listbox.Option
                                key={foreman._id}
                                value={foreman._id}
                                className={({ active }) =>
                                  `cursor-pointer select-none relative py-2 pl-10 pr-4 ${
                                    active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{foreman.contactId.name}</span>
                                    {selected && (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                        <Check className="w-5 h-5" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Listbox>
                        {form.formState.errors.foremanPersonnelIds && (
                          <p className="text-red-500 text-sm mt-1">
                            {form.formState.errors.foremanPersonnelIds.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setActiveTab("basic-info")}
                    disabled={isLoading}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("budget")}
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Budget Tab */}
            {activeTab === "budget" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Budget Hours</label>
                    <input
                      type="number"
                      {...form.register("budget.hours", {
                        setValueAs: (v) => (v === "" ? undefined : Number(v)),
                      })}
                    className="w-full border border-gray-300 p-2 rounded text-gray-900"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-gray-700 font-medium">Labour Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      {...form.register("budget.labourRate", {
                        setValueAs: (v) => (v === "" ? undefined : Number(v)),
                      })}
                    className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Currency</label>
                  <select
                    {...form.register("budget.currency")}
                      className="w-full border border-gray-300 p-2 rounded bg-white text-gray-900"
                    disabled={isLoading}
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setActiveTab("seats")}
                    disabled={isLoading}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("review")}
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Review Tab */}
            {activeTab === "review" && (
              <div className="space-y-4">
                <div className="border rounded-md p-4 space-y-2">
                  <h3 className="font-medium">Project Details</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-medium">Project Code:</span> {form.watch("projectCode")}
                    </div>
                    <div>
                      <span className="font-medium">Title:</span> {form.watch("title")}
                    </div>
                    <div>
                      <span className="font-medium">Client:</span>{" "}
                      {projectMeta?.clients.find((c) => c._id === form.watch("clientId"))?.name}
                    </div>
                    <div>
                      <span className="font-medium">Status:</span> {form.watch("status")}
                    </div>
                    <div>
                      <span className="font-medium">Location:</span> {form.watch("location") || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Bid Date:</span> {form.watch("bidDate") || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Award Date:</span> {form.watch("awardDate") || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Fabrication:</span> {form.watch("fabricationStartDate") || "N/A"} - {form.watch("fabricationEndDate") || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Erection:</span> {form.watch("erectionStartDate") || "N/A"} - {form.watch("erectionEndDate") || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Completion:</span> {form.watch("completionDate") || "N/A"}
                    </div>
                    <div>
                      <span className="font-medium">Budget Hours:</span> {form.watch("budget.hours") || "N/A"}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setActiveTab("budget")}
                    disabled={isLoading}
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg
                          className="mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </span>
                    ) : mode === "create" ? (
                      "Create Project"
                    ) : (
                      "Update Project"
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}
